import {
  PptxComponentDefaultsSchema
} from "./chunk-VLQAI2V6.js";

// src/schemas/theme.ts
import { Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  FontFamilyNameSchema,
  FontRegistrySchema,
  DesignSystemProperties,
  TextCaseSchema
} from "@json-to-office/shared";
import {
  ColorValueSchema,
  STYLE_NAMES
} from "@json-to-office/shared/schemas/slide-content";
import {
  ColorValueSchema as ColorValueSchema2,
  SEMANTIC_COLOR_NAMES,
  SEMANTIC_COLOR_ALIASES,
  STYLE_NAMES as STYLE_NAMES2,
  StyleNameSchema
} from "@json-to-office/shared/schemas/slide-content";
var BUILT_IN_PPTX_THEME_NAMES = [
  "consulting",
  "dark",
  "devportal",
  "minimal",
  "vermilion"
];
var GridMarginSchema = Type.Union(
  [
    Type.Number({ description: "Margin in inches (all sides)" }),
    Type.Object(
      {
        top: Type.Number({ description: "Top margin in inches" }),
        right: Type.Number({ description: "Right margin in inches" }),
        bottom: Type.Number({ description: "Bottom margin in inches" }),
        left: Type.Number({ description: "Left margin in inches" })
      },
      { additionalProperties: false }
    )
  ],
  { description: "Slide margins in inches" }
);
var GridGutterSchema = Type.Union(
  [
    Type.Number({ description: "Gutter in inches (both axes)" }),
    Type.Object(
      {
        column: Type.Number({ description: "Column gutter in inches" }),
        row: Type.Number({ description: "Row gutter in inches" })
      },
      { additionalProperties: false }
    )
  ],
  { description: "Gaps between grid tracks in inches" }
);
var GridConfigSchema = Type.Object(
  {
    columns: Type.Optional(
      Type.Number({
        minimum: 1,
        description: "Number of columns (default: 12)"
      })
    ),
    rows: Type.Optional(
      Type.Number({ minimum: 1, description: "Number of rows (default: 6)" })
    ),
    margin: Type.Optional(GridMarginSchema),
    gutter: Type.Optional(GridGutterSchema)
  },
  { additionalProperties: false, description: "Grid layout configuration" }
);
var HexColorSchema = Type.String({
  pattern: "^#?[0-9A-Fa-f]{6}$",
  description: "Hex color (e.g. #FF0000)"
});
var TextStyleSchema = Type.Object(
  {
    fontSize: Type.Optional(Type.Number()),
    fontFace: Type.Optional(FontFamilyNameSchema),
    fontColor: Type.Optional(ColorValueSchema),
    bold: Type.Optional(Type.Boolean()),
    fontWeight: Type.Optional(Type.Integer({ minimum: 100, maximum: 900 })),
    italic: Type.Optional(Type.Boolean()),
    align: Type.Optional(
      Type.Union([
        Type.Literal("left"),
        Type.Literal("center"),
        Type.Literal("right"),
        Type.Literal("justify")
      ])
    ),
    lineSpacing: Type.Optional(Type.Number()),
    charSpacing: Type.Optional(Type.Number()),
    paraSpaceAfter: Type.Optional(Type.Number()),
    paraSpaceBefore: Type.Optional(Type.Number()),
    case: Type.Optional(TextCaseSchema)
  },
  { additionalProperties: false, description: "Text style preset" }
);
var ThemeConfigSchema = Type.Object(
  {
    ...DesignSystemProperties,
    displayName: Type.Optional(Type.String()),
    description: Type.Optional(
      Type.String({
        description: "The visual voice: what the theme looks like, in one sentence."
      })
    ),
    whenToUse: Type.Optional(
      Type.String({
        description: "The decks the theme suits; discovery and the design guide quote it."
      })
    ),
    version: Type.Optional(Type.String()),
    name: Type.String({ description: "Theme name" }),
    colors: Type.Object(
      {
        primary: HexColorSchema,
        secondary: HexColorSchema,
        accent: HexColorSchema,
        background: HexColorSchema,
        text: HexColorSchema,
        text2: Type.Optional(HexColorSchema),
        background2: Type.Optional(HexColorSchema),
        accent4: Type.Optional(HexColorSchema),
        accent5: Type.Optional(HexColorSchema),
        accent6: Type.Optional(HexColorSchema)
      },
      {
        additionalProperties: false,
        description: "Theme color palette (10-slot scheme)"
      }
    ),
    fonts: Type.Object(
      {
        heading: FontFamilyNameSchema,
        body: FontFamilyNameSchema,
        mono: Type.Optional(FontFamilyNameSchema),
        light: Type.Optional(FontFamilyNameSchema)
      },
      { additionalProperties: false, description: "Font families" }
    ),
    fontRegistry: Type.Optional(FontRegistrySchema),
    defaults: Type.Object(
      {
        fontSize: Type.Number({ description: "Default font size in points" }),
        fontColor: HexColorSchema
      },
      { additionalProperties: false, description: "Default text styling" }
    ),
    styles: Type.Optional(
      Type.Partial(
        Type.Object(
          Object.fromEntries(
            STYLE_NAMES.map((n) => [n, TextStyleSchema])
          )
        ),
        { additionalProperties: false, description: "Named text style presets" }
      )
    ),
    componentDefaults: Type.Optional(PptxComponentDefaultsSchema)
  },
  {
    additionalProperties: false,
    description: "Presentation theme configuration"
  }
);
function isValidThemeConfig(data) {
  return Value.Check(ThemeConfigSchema, data);
}

export {
  BUILT_IN_PPTX_THEME_NAMES,
  GridMarginSchema,
  GridGutterSchema,
  GridConfigSchema,
  TextStyleSchema,
  ThemeConfigSchema,
  isValidThemeConfig,
  ColorValueSchema2 as ColorValueSchema,
  SEMANTIC_COLOR_NAMES,
  SEMANTIC_COLOR_ALIASES,
  STYLE_NAMES2 as STYLE_NAMES,
  StyleNameSchema
};
//# sourceMappingURL=chunk-VJY4O4VQ.js.map